import {
  ArrowBack,
  AssignmentOutlined,
  AttachMoneyOutlined,
  CalendarTodayOutlined,
  InfoOutlined,
  LocalShippingOutlined,
  PersonOutlined,
  ReceiptLongOutlined,
  ViewInArOutlined,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useNavigation } from "../../../hooks/useNavigation";
import { getOutsideOrderDetail } from "../../../service/OrderService";
import { tokens } from "../../../theme";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";

type OutsideOrderProductItem = {
  label?: string | null;
  quantity?: number | null;
  unitPrice?: number | string | null;
};

type OutsideOrderDetail = {
  orderId?: number | null;
  orderNumber?: string | null;
  orderDate?: string | null;
  dateOfService?: string | null;
  orderType?: string | null;
  orderStatus?: string | null;
  invoiceStatus?: string | null;
  practiceId?: number | null;
  practiceName?: string | null;
  patientId?: number | null;
  patientName?: string | null;
  productId?: number | null;
  practiceProductId?: number | null;
  productName?: string | null;
  productCategory?: string | null;
  productItems?: OutsideOrderProductItem[] | null;
  orderTotalUnit?: number | null;
  orderTotalPrice?: number | null;
  orderPrice?: number | string | null;
  shippingPrice?: number | string | null;
  total?: number | string | null;
  shippingAddress?: string | null;
  shippingAddressId?: number | null;
  shippingMethod?: string | null;
  shippingMethodId?: number | null;
  paymentMethod?: string | null;
  notes?: string | null;
  orderInfo?: Record<string, string> | null;
};

type OrderRouteState = {
  orderDetails?: {
    orderId?: number;
    orderNumber?: string;
  };
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const formatCurrency = (value?: number | string | null) => {
  const numericValue = Number(value ?? 0);
  return currencyFormatter.format(Number.isFinite(numericValue) ? numericValue : 0);
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
};

const formatText = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const OutsidePlatformOrderDetails = () => {
  const location = useLocation();
  const { id } = useParams();
  const { navigate } = useNavigation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { tokens: modalTokens, backBtnSx } = useModalStyles();
  const routeState = (location.state ?? {}) as OrderRouteState;
  const orderId = routeState.orderDetails?.orderId ?? (id ? Number(id) : undefined);

  const [order, setOrder] = useState<OutsideOrderDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchOutsideOrder = async () => {
      if (!orderId) {
        setErrorMessage("Order ID is missing.");
        return;
      }

      try {
        setIsLoading(true);
        setErrorMessage(null);
        const response = await getOutsideOrderDetail(Number(orderId));
        const responseData = response?.data?.data ?? response?.data;
        setOrder(responseData);
      } catch {
        setErrorMessage("Unable to load outside platform order details.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchOutsideOrder();
  }, [orderId]);

  const productItems = useMemo(() => order?.productItems ?? [], [order?.productItems]);
  const productItemsTotal = useMemo(
    () =>
      productItems.reduce((sum, item) => {
        const quantity = Number(item.quantity ?? 0);
        const unitPrice = Number(item.unitPrice ?? 0);
        return sum + quantity * unitPrice;
      }, 0),
    [productItems]
  );

  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${modalTokens.border}`,
    backgroundColor: modalTokens.bg,
    backgroundImage: "none",
    overflow: "hidden",
  };

  const InfoRow = ({ label, value }: { label: string; value?: string | number | null }) => (
    <Box
      display="flex"
      alignItems="flex-start"
      py={0.9}
      sx={{ borderBottom: `1px solid ${modalTokens.border}`, "&:last-child": { borderBottom: "none" } }}
    >
      <Typography sx={{ fontSize: "0.82rem", color: modalTokens.textSecondary, width: "42%", flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.88rem", color: modalTokens.textPrimary, fontWeight: 600, flex: 1 }}>
        {formatText(value)}
      </Typography>
    </Box>
  );

  const StatCard = ({
    icon,
    label,
    value,
    color,
  }: {
    icon: ReactNode;
    label: string;
    value: string | number;
    color: string;
  }) => (
    <Box
      sx={{
        p: 1.6,
        borderRadius: "14px",
        border: `1px solid ${modalTokens.border}`,
        background: `linear-gradient(135deg, ${color}22 0%, ${modalTokens.bg} 58%)`,
        display: "flex",
        alignItems: "center",
        gap: 1.4,
        minHeight: 74,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          backgroundColor: `${color}1f`,
          border: `1px solid ${color}44`,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          "& svg": { fontSize: "1.05rem" },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.68rem", color: modalTokens.textSecondary, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: "1rem", color: modalTokens.textPrimary, fontWeight: 800, mt: 0.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ mx: { xs: "10px", sm: "20px" }, my: "12px" }}>
      <Box mb={1.5} display="flex" alignItems="flex-start" gap={1.5}>
        <IconButton onClick={() => navigate("/order_list", { state: { withSearchHistory: true } })} size="small" sx={backBtnSx}>
          <ArrowBack sx={{ fontSize: "1.1rem" }} />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: modalTokens.textSecondary, textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.3 }}>
            Outside Platform Order
          </Typography>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: modalTokens.textPrimary, lineHeight: 1.1 }}>
            {order?.orderNumber ?? routeState.orderDetails?.orderNumber ?? "Order Details"}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mt={0.8} flexWrap="wrap">
            <Chip label={formatText(order?.orderStatus)} size="small" sx={{ backgroundColor: `${modalTokens.accent}18`, color: modalTokens.accent, border: `1px solid ${modalTokens.accent}33`, fontWeight: 700 }} />
            <Chip label={`Invoice: ${formatText(order?.invoiceStatus)}`} size="small" sx={{ backgroundColor: colors.grey[800], color: colors.grey[100], border: `1px solid ${colors.grey[700]}`, fontWeight: 700 }} />
          </Box>
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={260}>
          <CircularProgress size={28} />
        </Box>
      ) : errorMessage ? (
        <Box sx={{ ...cardSx, p: 2.5 }}>
          <Typography sx={{ color: theme.palette.error.main, fontWeight: 700 }}>{errorMessage}</Typography>
        </Box>
      ) : (
        <>
        <Grid container spacing={1.5} mb={1.5}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<ViewInArOutlined />} label="Total Items" value={productItems.length} color="#8b5cf6" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<ReceiptLongOutlined />} label="Invoice Status" value={formatText(order?.invoiceStatus)} color="#3b82f6" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<CalendarTodayOutlined />} label="Date of Service" value={formatDate(order?.dateOfService)} color="#14b8a6" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard icon={<AttachMoneyOutlined />} label="Total Amount" value={formatCurrency(order?.total)} color="#f59e0b" />
          </Grid>
        </Grid>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" }, gap: 1.5, alignItems: "flex-start" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={cardSx}>
              <SectionHeader icon={<AssignmentOutlined />} title="Order Information" />
              <Box sx={{ px: 2.8, py: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <InfoRow label="Order Number" value={order?.orderNumber} />
                    <InfoRow label="Order Date" value={formatDate(order?.orderDate)} />
                    <InfoRow label="Date of Service" value={formatDate(order?.dateOfService)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoRow label="Order Status" value={order?.orderStatus} />
                    <InfoRow label="Invoice Status" value={order?.invoiceStatus} />
                    <InfoRow label="Total Units" value={order?.orderTotalUnit} />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            <Box sx={cardSx}>
              <SectionHeader icon={<PersonOutlined />} title="References" />
              <Box sx={{ px: 2.8, py: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <InfoRow label="Practice" value={order?.practiceName} />
                    <InfoRow label="Patient" value={order?.patientName} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InfoRow label="Product" value={order?.productName} />
                    <InfoRow label="Quantity" value={order?.orderTotalUnit} />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            <Box sx={cardSx}>
              <SectionHeader
                icon={<ViewInArOutlined />}
                title="Product Items"
                action={
                  <Chip
                    label={`${order?.orderTotalUnit ?? productItems.reduce((sum, item) => sum + Number(item.quantity ?? 0), 0)} total units`}
                    size="small"
                    sx={{ backgroundColor: `${modalTokens.accent}18`, color: modalTokens.accent, border: `1px solid ${modalTokens.accent}33`, fontWeight: 800 }}
                  />
                }
              />
              <TableContainer sx={{ px: 1.4, py: 1.4 }}>
                <Table size="small" sx={{ borderCollapse: "separate", borderSpacing: "0 8px" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: modalTokens.textSecondary, fontSize: "0.7rem", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "none" }}>Item / Size</TableCell>
                      <TableCell align="center" sx={{ color: modalTokens.textSecondary, fontSize: "0.7rem", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "none" }}>Qty</TableCell>
                      <TableCell align="right" sx={{ color: modalTokens.textSecondary, fontSize: "0.7rem", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "none" }}>Unit Price</TableCell>
                      <TableCell align="right" sx={{ color: modalTokens.textSecondary, fontSize: "0.7rem", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "none" }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productItems.length > 0 ? productItems.map((item, index) => {
                      const quantity = Number(item.quantity ?? 0);
                      const unitPrice = Number(item.unitPrice ?? 0);
                      return (
                        <TableRow key={`${item.label ?? "item"}-${index}`}>
                          <TableCell sx={{ color: modalTokens.textPrimary, fontWeight: 700, backgroundColor: modalTokens.cardBg, borderTopLeftRadius: "12px", borderBottomLeftRadius: "12px", borderBottom: `1px solid ${modalTokens.border}`, borderTop: `1px solid ${modalTokens.border}`, borderLeft: `1px solid ${modalTokens.border}` }}>
                            <Box display="flex" alignItems="center" gap={1.1}>
                              <Box sx={{ width: 30, height: 30, borderRadius: "8px", backgroundColor: `${modalTokens.accent}18`, border: `1px solid ${modalTokens.accent}33`, color: modalTokens.accent, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: "0.76rem", flexShrink: 0 }}>
                                {index + 1}
                              </Box>
                              <Typography sx={{ fontSize: "0.88rem", fontWeight: 800, color: modalTokens.textPrimary }}>
                                {formatText(item.label)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ color: modalTokens.textPrimary, backgroundColor: modalTokens.cardBg, borderBottom: `1px solid ${modalTokens.border}`, borderTop: `1px solid ${modalTokens.border}` }}>
                            <Chip label={quantity} size="small" sx={{ height: 22, minWidth: 34, backgroundColor: "rgba(139,92,246,0.16)", color: "#c4b5fd", border: "1px solid rgba(139,92,246,0.28)", fontWeight: 800 }} />
                          </TableCell>
                          <TableCell align="right" sx={{ color: modalTokens.textPrimary, fontWeight: 700, backgroundColor: modalTokens.cardBg, borderBottom: `1px solid ${modalTokens.border}`, borderTop: `1px solid ${modalTokens.border}` }}>{formatCurrency(unitPrice)}</TableCell>
                          <TableCell align="right" sx={{ color: modalTokens.accent, fontWeight: 900, backgroundColor: modalTokens.cardBg, borderTopRightRadius: "12px", borderBottomRightRadius: "12px", borderBottom: `1px solid ${modalTokens.border}`, borderTop: `1px solid ${modalTokens.border}`, borderRight: `1px solid ${modalTokens.border}` }}>{formatCurrency(quantity * unitPrice)}</TableCell>
                        </TableRow>
                      );
                    }) : (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ color: modalTokens.textSecondary, textAlign: "center", py: 3 }}>
                          No product items available.
                        </TableCell>
                      </TableRow>
                    )}
                    {productItems.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="right" sx={{ color: modalTokens.textSecondary, fontWeight: 900, borderBottom: "none", pt: 1.2 }}>
                          Items Total
                        </TableCell>
                        <TableCell align="right" sx={{ color: modalTokens.textPrimary, fontSize: "1rem", fontWeight: 900, borderBottom: "none", pt: 1.2 }}>
                          {formatCurrency(order?.orderTotalPrice ?? productItemsTotal)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>

          <Box sx={{ position: "sticky", top: 12, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={cardSx}>
              <SectionHeader icon={<AttachMoneyOutlined />} iconColor="#D97706" title="Order Summary" />
              <Box sx={{ px: 2.8, py: 1 }}>
                <InfoRow label="Items Total" value={formatCurrency(order?.orderTotalPrice ?? productItemsTotal)} />
                <InfoRow label="Order Price" value={formatCurrency(order?.orderPrice)} />
                <InfoRow label="Shipping Price" value={formatCurrency(order?.shippingPrice)} />
                <InfoRow label="Total" value={formatCurrency(order?.total)} />
              </Box>
            </Box>

            <Box sx={cardSx}>
              <SectionHeader icon={<LocalShippingOutlined />} title="Shipping" />
              <Box sx={{ px: 2.8, py: 1 }}>
                <InfoRow label="Method" value={order?.shippingMethod} />
                <InfoRow label="Address" value={order?.shippingAddress} />
              </Box>
            </Box>

            <Box sx={cardSx}>
              <SectionHeader icon={<InfoOutlined />} title="Notes" />
              <Box sx={{ px: 2.8, py: 1.5 }}>
                <Typography sx={{ fontSize: "0.88rem", color: order?.notes ? modalTokens.textPrimary : modalTokens.textSecondary, whiteSpace: "pre-wrap" }}>
                  {order?.notes || "No notes available."}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        </>
      )}
    </Box>
  );
};

export default OutsidePlatformOrderDetails;
